<template>
  <div class="ovg-page">
    <div class="ovg-page__bg" aria-hidden="true" />

    <!-- Header -->
    <header class="ovg-header">
      <div
        class="ovg-header__logos"
        @click="$router.push({ name: 'ovg-landing' })"
      >
        <img
          src="@/assets/logo_original.svg"
          alt="Samtek"
          class="ovg-header__logo-samtek"
        />
        <img
          src="@/assets/logo_asus.png"
          alt="ASUS"
          class="ovg-header__logo-asus"
        />
      </div>
      <button
        class="ovg-header__salir"
        @click="$router.push({ name: 'ovg-landing' })"
      >
        Salir <font-awesome-icon icon="sign-out-alt" />
      </button>
    </header>

    <!-- Sub-header -->
    <div class="ovg-subheader">
      <div class="ovg-subheader__inner">
        <h2 class="ovg-subheader__title">
          Adquiere tu Extensión de Garantía Oficial
        </h2>
        <p class="ovg-subheader__subtitle">
          Asegura la vida útil de tu equipo por más tiempo.<br />
          Selecciona tu modelo e ingresa los datos de tu equipo.
        </p>
      </div>
    </div>

    <!-- Form card -->
    <div class="ovg-content">
      <div class="ovg-card">
        <!-- Mobile progress bar (hidden on desktop) -->
        <div class="ovg-progress">
          <div class="ovg-progress__track">
            <div class="ovg-progress__fill" style="width: 50%" />
          </div>
          <span class="ovg-progress__label">Paso Actual: Tu Equipo</span>
        </div>

        <!-- Step indicator (hidden on mobile) -->
        <div class="ovg-steps">
          <div class="ovg-step ovg-step--done">
            <span class="ovg-step__num">1</span>
            <span class="ovg-step__label">Tus Datos</span>
          </div>
          <div class="ovg-step ovg-step--active">
            <span class="ovg-step__num">2</span>
            <span class="ovg-step__label">Tu Equipo</span>
          </div>
          <div class="ovg-step">
            <span class="ovg-step__num">3</span>
            <span class="ovg-step__label">Ext. Garantía</span>
          </div>
          <div class="ovg-step">
            <span class="ovg-step__num">4</span>
            <span class="ovg-step__label">Protección Adicional</span>
          </div>
        </div>

        <form @submit.prevent="siguiente">
          <!-- N° serie + Modelo -->
          <div class="ovg-row">
            <div class="ovg-field">
              <label class="ovg-label">N° de serie del equipo*</label>
              <input
                v-model="form.numero_serie"
                type="text"
                placeholder="S/N 123456789"
                maxlength="15"
                class="ovg-input"
                :class="{ 'ovg-input--error': serieError }"
                @input="onSerieInput"
                @blur="onSerieBlur"
              />
              <span v-if="serieError" class="ovg-error">{{
                serieErrorMessage
              }}</span>
            </div>

            <!-- Autocomplete modelo -->
            <div
              class="ovg-field ovg-modelo-wrap"
              v-click-outside="cerrarDropdown"
            >
              <label class="ovg-label">Modelo del Equipo*</label>
              <div
                class="ovg-input ovg-modelo-input"
                :class="{ 'ovg-input--error': modeloError }"
              >
                <input
                  v-model="modeloQuery"
                  type="text"
                  placeholder="Buscar modelo..."
                  class="ovg-modelo-input__field"
                  @input="onModeloInput"
                  @focus="onModeloFocus"
                  @click="onModeloFocus"
                />
                <!-- Spinner cuando carga -->
                <span v-if="loadingModelos" class="ovg-modelo-spinner" />
                <!-- X para limpiar cuando hay texto y no carga -->
                <button
                  v-else-if="modeloQuery.length > 0"
                  type="button"
                  class="ovg-modelo-clear"
                  @mousedown.prevent="limpiarModelo"
                >
                  <font-awesome-icon icon="times" />
                </button>
                <!-- Chevron cuando vacío y sin carga -->
                <font-awesome-icon
                  v-else
                  icon="chevron-down"
                  class="ovg-modelo-input__icon"
                />
              </div>
              <span v-if="modeloError" class="ovg-error"
                >Selecciona un modelo</span
              >

              <!-- Dropdown -->
              <div
                v-if="dropdownVisible"
                class="ovg-dropdown"
                @scroll.passive="onDropdownScroll"
              >
                <!-- Loading inicial -->
                <div
                  v-if="loadingModelos && modelos.length === 0"
                  class="ovg-dropdown__loading"
                >
                  <span class="ovg-dropdown__spinner" /> Buscando...
                </div>

                <template v-else>
                  <!-- "Mi modelo no está en la lista" — primera opción, solo con texto -->
                  <div
                    v-if="modeloQuery.trim()"
                    class="ovg-dropdown__no-modelo"
                    @mousedown.prevent="seleccionarNoEnLista"
                  >
                    + Mi modelo no está en la lista
                  </div>

                  <div
                    v-for="modelo in modelos"
                    :key="modelo.id"
                    class="ovg-dropdown__item"
                    @mousedown.prevent="seleccionarModelo(modelo)"
                  >
                    {{ modelo.nombre }}
                  </div>

                  <div v-if="loadingMore" class="ovg-dropdown__loading">
                    Cargando más...
                  </div>
                </template>
              </div>
            </div>
          </div>

          <!-- Fecha de compra del equipo (OVG09) -->
          <div class="ovg-row">
            <div class="ovg-field ovg-field--half">
              <label class="ovg-label">Fecha de compra del equipo*</label>
              <b-form-datepicker
                v-model="form.fecha_compra_equipo"
                locale="es-CL"
                :max="hoyISO"
                placeholder="Ingrese una fecha"
                class="ovg-input"
                :class="{ 'ovg-input--error': fechaError }"
                @input="fechaError = false"
              />
              <span v-if="fechaError" class="ovg-error">{{
                fechaErrorMsg
              }}</span>
            </div>
          </div>

          <!-- Boleta upload -->
          <div class="ovg-field">
            <label class="ovg-label">Boleta/Factura de compra*</label>
            <div
              class="ovg-upload"
              :class="{
                'ovg-upload--error': boletaError,
                'ovg-upload--filled': boletaFile,
              }"
              @dragover.prevent
              @drop.prevent="onDrop"
              @click="triggerFileInput"
            >
              <template v-if="!boletaFile">
                <font-awesome-icon
                  icon="cloud-upload-alt"
                  class="ovg-upload__icon"
                />
                <p class="ovg-upload__text">
                  Arrastra y suelta tu archivo aquí.
                </p>
                <span class="ovg-btn ovg-btn--primary ovg-upload__btn">
                  Seleccionar desde tu equipo
                </span>
                <p class="ovg-upload__note">
                  Formatos aceptados: JPG, PNG o PDF. Peso máximo: 5 MB.
                </p>
              </template>
              <template v-else>
                <img
                  v-if="boletaPreview"
                  :src="boletaPreview"
                  class="ovg-upload__preview-img"
                  alt="Vista previa boleta"
                />
                <div v-else class="ovg-upload__preview-file">
                  <font-awesome-icon
                    icon="file-pdf"
                    class="ovg-upload__preview-icon"
                  />
                  <span>{{ boletaFile.name }}</span>
                </div>
                <button
                  type="button"
                  class="ovg-upload__remove"
                  @click.stop="removeBoleta"
                >
                  <font-awesome-icon icon="times" /> Cambiar archivo
                </button>
              </template>
            </div>
            <span v-if="boletaError" class="ovg-error"
              >La boleta es requerida</span
            >
            <input
              ref="fileInput"
              type="file"
              accept="image/jpeg,image/png,application/pdf"
              class="ovg-upload__input"
              @change="onFileChange"
            />
          </div>

          <!-- Submit -->
          <div class="ovg-card__footer ovg-card__footer--split">
            <button
              type="button"
              class="ovg-btn ovg-btn--secondary"
              @click="$router.push({ name: 'ovg-cliente' })"
            >
              Volver
            </button>
            <button type="submit" class="ovg-btn ovg-btn--primary">
              Siguiente Paso
            </button>
          </div>
        </form>
      </div>

      <resumen-solicitud-panel />
    </div>

    <!-- Mobile bottom bar + sheet (hidden on desktop) -->
    <ovg-resumen-mobile-bar />

    <!-- Contact button (WhatsApp): oculto hasta que el negocio lo habilite
    <a
      href="https://wa.me"
      class="ovg-contact-btn"
      target="_blank"
      rel="noopener"
    >
      <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22">
        <path :d="waSvgPath" />
      </svg>
    </a>
    -->

    <p class="ovg-page__footer">
      © 2026 Samtek | Servicio Técnico ASUS Autorizado en Chile.
    </p>

    <!-- Modal 1: Solicitar nuevo modelo -->
    <div
      v-if="modalSolicitudVisible"
      class="ovg-modal-backdrop"
      @click.self="cerrarModalSolicitud"
    >
      <div class="ovg-modal">
        <div class="ovg-modal__header">
          <h3 class="ovg-modal__title">Solicitar nuevo modelo de equipo</h3>
          <button
            type="button"
            class="ovg-modal__close"
            @click="cerrarModalSolicitud"
          >
            <font-awesome-icon icon="times" />
          </button>
        </div>
        <p class="ovg-modal__subtitle">
          No te preocupes, si no encuentras tu modelo, dinos cuál es y te
          avisaremos cuando la garantía esté disponible.
        </p>

        <div class="ovg-field">
          <label class="ovg-label">Nombre del Modelo*</label>
          <input
            v-model="solicitud.nombre_modelo"
            type="text"
            class="ovg-input"
            :class="{
              'ovg-input--error':
                solicitudSubmitted && !solicitud.nombre_modelo,
            }"
          />
          <span
            v-if="solicitudSubmitted && !solicitud.nombre_modelo"
            class="ovg-error"
            >El nombre del modelo es requerido</span
          >
        </div>

        <div class="ovg-field">
          <label class="ovg-label">Garantía Deseada*</label>
          <div class="ovg-select-wrap">
            <select
              v-model="solicitud.garantia_deseada_tipo"
              class="ovg-input ovg-select"
              :class="{
                'ovg-input--error':
                  solicitudSubmitted && !solicitud.garantia_deseada_tipo,
              }"
            >
              <option value="" disabled>Seleccione</option>
              <option
                v-for="tipo in tiposGarantia"
                :key="tipo.value"
                :value="tipo.value"
              >
                {{ tipo.label }}
              </option>
            </select>
            <font-awesome-icon
              icon="chevron-down"
              class="ovg-select-wrap__icon"
            />
          </div>
          <span
            v-if="solicitudSubmitted && !solicitud.garantia_deseada_tipo"
            class="ovg-error"
            >Selecciona una garantía</span
          >
        </div>

        <div class="ovg-field">
          <label class="ovg-label">Comentario</label>
          <textarea
            v-model="solicitud.comentario"
            placeholder="Opcional"
            class="ovg-input ovg-textarea"
          />
        </div>

        <div class="ovg-modal__footer">
          <button
            type="button"
            class="ovg-btn ovg-btn--secondary"
            @click="cerrarModalSolicitud"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="ovg-btn ovg-btn--primary"
            :disabled="enviandoSolicitud"
            @click="enviarSolicitud"
          >
            {{
              enviandoSolicitud ? 'Enviando...' : 'Enviar solicitud de modelo'
            }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal 2: Confirmación -->
    <div v-if="modalConfirmacionVisible" class="ovg-modal-backdrop">
      <div class="ovg-modal ovg-modal--confirmacion">
        <div class="ovg-modal__header">
          <h3 class="ovg-modal__title">¡Solicitud recibida con éxito!</h3>
          <button
            type="button"
            class="ovg-modal__close"
            @click="volverAlInicio"
          >
            <font-awesome-icon icon="times" />
          </button>
        </div>
        <font-awesome-icon icon="check-circle" class="ovg-modal__check" />
        <p class="ovg-modal__confirm-text">
          Gracias por informarnos. Revisaremos el modelo de tu equipo y te
          avisaremos por correo electrónico apenas tengamos garantías
          disponibles para ti.
        </p>
        <div class="ovg-modal__footer ovg-modal__footer--center">
          <button
            type="button"
            class="ovg-btn ovg-btn--primary"
            @click="volverAlInicio"
          >
            Volver al inicio
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import debounce from '@/utils/debounce'
import ResumenSolicitudPanel from '@/components/OVG/ResumenSolicitudPanel.vue'
import OvgResumenMobileBar from '@/components/OVG/OvgResumenMobileBar.vue'

// Debe coincidir con la validación de OVGCreateSerializer.validate_numero_serie
// en el backend (garantias/serializers.py).
const SERIE_LENGTH = 15

export default {
  name: 'ModeloSearchView',
  components: {
    ResumenSolicitudPanel,
    OvgResumenMobileBar,
  },
  directives: {
    'click-outside': {
      bind(el, binding) {
        el.clickOutsideHandler = (e) => {
          if (!el.contains(e.target)) binding.value()
        }
        document.addEventListener('mousedown', el.clickOutsideHandler)
      },
      unbind(el) {
        document.removeEventListener('mousedown', el.clickOutsideHandler)
      },
    },
  },
  data() {
    return {
      waSvgPath:
        // eslint-disable-next-line max-len
        'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z',
      form: {
        numero_serie: '',
        modelo_id: null,
        modelo_nombre: '',
        fecha_compra_equipo: '',
      },
      serieError: false,
      modeloError: false,
      fechaError: false,
      fechaErrorMsg: '',
      boletaError: false,
      boletaFile: null,
      boletaPreview: null,
      modeloQuery: '',
      modelos: [],
      dropdownVisible: false,
      loadingModelos: false,
      loadingMore: false,
      nextCursor: null,
      hasMore: false,
      // Elección "mi modelo no está en la lista" (el modal se abre en Siguiente)
      noEstaEnLista: false,
      // Modal solicitud
      modalSolicitudVisible: false,
      solicitudSubmitted: false,
      enviandoSolicitud: false,
      solicitud: {
        nombre_modelo: '',
        garantia_deseada_tipo: '',
        comentario: '',
      },
      // 3 tipos fijos (OVG02) — alineados con los choices del backend
      tiposGarantia: [
        { value: 'extension_tiempo', label: 'Extensión de tiempo' },
        { value: 'danos_accidentales', label: 'Daños accidentales' },
        { value: 'atencion_onsite', label: 'Atención On Site' },
      ],
      // Modal confirmación
      modalConfirmacionVisible: false,
    }
  },
  created() {
    // Sin datos del paso 1 (recarga de página): volver a Tus Datos
    if (!this.$store.getters['OVGStore/cliente']) {
      this.$router.replace({ name: 'ovg-cliente' })
      return
    }
    this.debouncedSearch = debounce(this.ejecutarBusqueda, 300)
    const equipo = this.$store.getters['OVGStore/equipo']
    if (equipo) {
      this.form = { ...equipo }
      this.modeloQuery = equipo.modelo_nombre || ''
    }
    // Al volver desde el paso 3, re-hidratar la boleta ya subida (persiste en
    // el store) para no perder el archivo que el usuario cargó.
    const boleta = this.$store.getters['OVGStore/boleta']
    if (boleta) this.setFile(boleta)
  },
  computed: {
    // Fecha de hoy (YYYY-MM-DD) para limitar el date picker (no futura).
    hoyISO() {
      const d = new Date()
      const mes = String(d.getMonth() + 1).padStart(2, '0')
      const dia = String(d.getDate()).padStart(2, '0')
      return `${d.getFullYear()}-${mes}-${dia}`
    },
    serieErrorMessage() {
      if (!this.form.numero_serie) return 'El N° de serie es requerido'
      return `El N° de serie debe tener ${SERIE_LENGTH} caracteres`
    },
  },
  methods: {
    // --- Validación fecha de compra del equipo ---
    validarFecha() {
      if (!this.form.fecha_compra_equipo) {
        this.fechaError = true
        this.fechaErrorMsg = 'La fecha de compra del equipo es requerida'
        return false
      }
      if (this.form.fecha_compra_equipo > this.hoyISO) {
        this.fechaError = true
        this.fechaErrorMsg = 'La fecha de compra no puede ser futura'
        return false
      }
      this.fechaError = false
      return true
    },
    // --- N° serie ---
    onSerieInput(e) {
      this.form.numero_serie = e.target.value.replace(/\s/g, '')
      this.serieError = false
    },
    onSerieBlur() {
      this.form.numero_serie = this.form.numero_serie.trim()
      this.serieError = this.form.numero_serie.length !== SERIE_LENGTH
    },

    // --- Autocomplete ---
    limpiarModelo() {
      this.modeloQuery = ''
      this.form.modelo_id = null
      this.form.modelo_nombre = ''
      this.modelos = []
      this.noEstaEnLista = false
      this.dropdownVisible = false
    },
    onModeloInput() {
      this.form.modelo_id = null
      this.form.modelo_nombre = ''
      this.noEstaEnLista = false
      this.modelos = []
      this.nextCursor = null
      this.hasMore = false
      if (!this.modeloQuery.trim()) {
        this.dropdownVisible = false
        this.loadingModelos = false
        return
      }
      this.dropdownVisible = true
      // Mostrar el spinner de inmediato durante la ventana de debounce.
      this.loadingModelos = true
      this.debouncedSearch()
    },
    onModeloFocus() {
      // Con texto: reabrir el dropdown si ya hay resultados.
      if (this.modeloQuery.trim()) {
        if (this.modelos.length) this.dropdownVisible = true
        return
      }
      // Sin texto: mostrar los primeros modelos disponibles.
      this.dropdownVisible = true
      if (!this.modelos.length) this.cargarPrimeros()
    },
    cargarPrimeros() {
      this.loadingModelos = true
      this.$store
        .dispatch('OVGStore/buscarModelos', { q: '', cursor: null, limit: 10 })
        .then((data) => {
          this.modelos = data.results || []
          this.nextCursor = data.next_cursor || null
          this.hasMore = data.has_more || false
        })
        .catch(() => {})
        .finally(() => {
          this.loadingModelos = false
        })
    },
    ejecutarBusqueda() {
      if (!this.modeloQuery.trim()) return
      this.loadingModelos = true
      this.$store
        .dispatch('OVGStore/buscarModelos', {
          q: this.modeloQuery,
          cursor: null,
        })
        .then((data) => {
          this.modelos = data.results || []
          this.nextCursor = data.next_cursor || null
          this.hasMore = data.has_more || false
        })
        .catch(() => {})
        .finally(() => {
          this.loadingModelos = false
        })
    },
    onDropdownScroll(e) {
      if (!this.hasMore || this.loadingMore) return
      const el = e.target
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
        this.cargarMas()
      }
    },
    cargarMas() {
      if (!this.hasMore || !this.nextCursor || this.loadingMore) return
      this.loadingMore = true
      this.$store
        .dispatch('OVGStore/buscarModelos', {
          q: this.modeloQuery,
          cursor: this.nextCursor,
        })
        .then((data) => {
          this.modelos = this.modelos.concat(data.results || [])
          this.nextCursor = data.next_cursor || null
          this.hasMore = data.has_more || false
        })
        .catch(() => {})
        .finally(() => {
          this.loadingMore = false
        })
    },
    seleccionarModelo(modelo) {
      this.form.modelo_id = modelo.id
      this.form.modelo_nombre = modelo.nombre
      this.modeloQuery = modelo.nombre
      this.noEstaEnLista = false
      this.dropdownVisible = false
      this.modeloError = false
    },
    cerrarDropdown() {
      this.dropdownVisible = false
    },

    // --- Boleta ---
    triggerFileInput() {
      if (!this.boletaFile) this.$refs.fileInput.click()
    },
    onFileChange(e) {
      const file = e.target.files[0]
      if (file) this.setFile(file)
    },
    onDrop(e) {
      const file = e.dataTransfer.files[0]
      if (file) this.setFile(file)
    },
    setFile(file) {
      const allowed = ['image/jpeg', 'image/png', 'application/pdf']
      if (!allowed.includes(file.type)) return
      if (file.size > 5 * 1024 * 1024) return
      this.boletaFile = file
      this.boletaError = false
      if (file.type !== 'application/pdf') {
        const reader = new FileReader()
        reader.onload = (e) => {
          this.boletaPreview = e.target.result
        }
        reader.readAsDataURL(file)
      } else {
        this.boletaPreview = null
      }
    },
    removeBoleta() {
      this.boletaFile = null
      this.boletaPreview = null
      this.$refs.fileInput.value = ''
    },

    // --- Submit principal ---
    siguiente() {
      this.serieError = this.form.numero_serie.length !== SERIE_LENGTH
      this.boletaError = !this.boletaFile
      const fechaOk = this.validarFecha()

      // Flujo "mi modelo no está en la lista": solo exige serie + fecha + boleta
      // y abre el modal (el equipo no existe, no hay modelo que validar).
      if (this.noEstaEnLista) {
        this.modeloError = false
        if (this.serieError || !fechaOk || this.boletaError) return
        this.abrirModalSolicitud()
        return
      }

      this.modeloError = !this.form.modelo_id
      if (this.serieError || this.modeloError || !fechaOk || this.boletaError)
        return

      this.$store.commit('OVGStore/SET_EQUIPO', { ...this.form })
      this.$store.commit('OVGStore/SET_BOLETA', this.boletaFile)
      this.$store.commit(
        'OVGStore/SET_FECHA_COMPRA_EQUIPO',
        this.form.fecha_compra_equipo
      )
      this.$router.push({ name: 'ovg-catalogo' })
    },

    // --- Modal solicitud modelo ---
    seleccionarNoEnLista() {
      // Solo marca la elección en el input; el modal se abre en "Siguiente Paso"
      // (después de que el usuario suba la boleta, que va más abajo en el form).
      this.noEstaEnLista = true
      this.modeloQuery = 'Mi modelo no está en la lista'
      this.form.modelo_id = null
      this.form.modelo_nombre = ''
      this.modeloError = false
      this.dropdownVisible = false
    },
    abrirModalSolicitud() {
      this.solicitudSubmitted = false
      this.solicitud = {
        nombre_modelo: '',
        garantia_deseada_tipo: '',
        comentario: '',
      }
      this.dropdownVisible = false
      this.modalSolicitudVisible = true
    },
    cerrarModalSolicitud() {
      this.modalSolicitudVisible = false
    },
    enviarSolicitud() {
      this.solicitudSubmitted = true
      if (
        !this.solicitud.nombre_modelo ||
        !this.solicitud.garantia_deseada_tipo
      )
        return

      const cliente = this.$store.getters['OVGStore/cliente'] || {}
      // La boleta se sube localmente en esta vista; solo se commitea al store
      // en el flujo "Siguiente Paso". Para la solicitud usamos el File local.
      const boleta = this.boletaFile || this.$store.getters['OVGStore/boleta']

      const fd = new FormData()
      fd.append('correo_cliente', cliente.correo || '')
      fd.append('nombre_cliente', cliente.nombre || '')
      fd.append('apellido_cliente', cliente.apellido || '')
      fd.append('rut_cliente', cliente.rut || '')
      fd.append('telefono_cliente', cliente.telefono || '')
      fd.append('numero_serie', this.form.numero_serie)
      // Fecha de compra del equipo (OVG09): se toma del formulario del paso 2,
      // NO de un campo dentro del modal.
      fd.append('fecha_compra_equipo', this.form.fecha_compra_equipo || '')
      fd.append('nombre_modelo_solicitado', this.solicitud.nombre_modelo)
      fd.append('garantia_deseada_tipo', this.solicitud.garantia_deseada_tipo)
      if (this.solicitud.comentario)
        fd.append('comentario', this.solicitud.comentario)
      if (boleta) fd.append('boleta_cliente', boleta)

      this.enviandoSolicitud = true
      this.$store
        .dispatch('OVGStore/solicitarCreacionModelo', fd)
        .then(() => {
          // Limpiar el store al enviar con éxito (no en "Volver al inicio").
          this.$store.commit('OVGStore/RESET_STATE')
          this.modalSolicitudVisible = false
          this.modalConfirmacionVisible = true
        })
        .catch(() => {})
        .finally(() => {
          this.enviandoSolicitud = false
        })
    },

    // --- Modal confirmación ---
    volverAlInicio() {
      this.$router.push({ name: 'ovg-landing' })
    },
  },
}
</script>

<style scoped>
.ovg-page {
  font-family: 'Noto Sans', sans-serif;
  min-height: 100vh;
  background-color: #f5f6fa;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  padding-bottom: 80px;
}

.ovg-page__bg {
  /* Sin `opacity`: el PNG ya es una marca de agua (su patrón viene con
     alpha 13/255). Desvanecerlo otra vez acá lo dejaba en 0,62% de
     contraste, o sea invisible en pantalla. */
  position: fixed;
  inset: 0;
  background-image: url('~@/assets/fondo_samtek.png');
  background-size: cover;
  background-position: center;
  pointer-events: none;
  z-index: 0;
}

/* Header */
.ovg-header {
  position: relative;
  z-index: 1;
  width: 100%;
  height: 55px;
  background: #ffffff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 162px;
  flex-shrink: 0;
}

.ovg-header__logos {
  display: flex;
  align-items: center;
  gap: 15px;
  cursor: pointer;
}

.ovg-header__logo-samtek {
  height: 18px;
  width: auto;
}

.ovg-header__logo-asus {
  height: 18px;
  width: auto;
}

.ovg-header__salir {
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: #23201c;
  font-family: 'Noto Sans', sans-serif;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  padding: 0;
}

/* Sub-header */
.ovg-subheader {
  position: relative;
  z-index: 1;
  width: 100%;
  background: #0a192f;
  min-height: 195px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px 20px;
  text-align: left;
}

/* Alinea el texto con el borde izquierdo de la card del formulario. */
.ovg-subheader__inner {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 1207px;
  max-width: calc(100% - 40px);
}

.ovg-subheader__title {
  font-size: 28px;
  font-weight: 700;
  color: #f5f6fa;
  margin: 0;
}

.ovg-subheader__subtitle {
  font-size: 15px;
  font-weight: 400;
  color: #f5f6fa;
  margin: 0;
  line-height: 1.5;
}

/* Content: card + side panel */
.ovg-content {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  gap: 20px;
  width: 1207px;
  max-width: calc(100% - 40px);
  margin-top: -20px;
}

.ovg-content >>> .ovg-resumen {
  position: sticky;
  top: 20px;
}

/* Card */
.ovg-card {
  position: relative;
  z-index: 1;
  background: #ffffff;
  border-radius: 8px;
  padding: 20px;
  width: 827px;
  flex-shrink: 0;
  box-shadow: 0px 8px 12px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  gap: 24px;
}

@media (max-width: 1247px) {
  .ovg-content {
    flex-direction: column;
    align-items: center;
    width: 827px;
  }

  .ovg-subheader__inner {
    width: 827px;
  }

  .ovg-content >>> .ovg-resumen {
    position: static;
    width: 100%;
  }
}

/* Steps */
.ovg-steps {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.ovg-step {
  display: flex;
  align-items: center;
  gap: 10px;
}

.ovg-step__num {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 60px;
  background: #f5f6fa;
  color: #525252;
  font-size: 18px;
  font-weight: 700;
  flex-shrink: 0;
}

.ovg-step__label {
  font-size: 20px;
  font-weight: 700;
  color: #525252;
  white-space: nowrap;
}

.ovg-step--active .ovg-step__num,
.ovg-step--done .ovg-step__num {
  background: #fece2f;
  color: #23201c;
  font-size: 15px;
}

.ovg-step--active .ovg-step__label,
.ovg-step--done .ovg-step__label {
  color: #23201c;
}

/* Form layout */
form {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* Form fields */
.ovg-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  position: relative;
}

.ovg-row {
  display: flex;
  gap: 20px;
}

/* Campo que ocupa media fila: mismo ancho que N° de serie / Modelo. */
.ovg-field--half {
  flex: 0 0 calc(50% - 10px);
  max-width: calc(50% - 10px);
}

.ovg-label {
  font-size: 15px;
  font-weight: 700;
  color: #23201c;
  margin: 0;
}

.ovg-input {
  height: 45px;
  border: 1px solid #525252;
  border-radius: 5px;
  padding: 0 10px;
  font-family: 'Noto Sans', sans-serif;
  font-size: 15px;
  color: #23201c;
  background: #ffffff;
  width: 100%;
  outline: none;
  transition: border-color 0.15s;
  box-sizing: border-box;
}

.ovg-input::placeholder {
  color: #525252;
  font-weight: 400;
}

.ovg-input:focus {
  border-color: #fece2f;
}

.ovg-input--error {
  border-color: #dc3545;
}

.ovg-error {
  font-size: 13px;
  color: #dc3545;
}

/* Autocomplete */
.ovg-modelo-wrap {
  position: relative;
}

.ovg-modelo-input {
  display: flex;
  align-items: center;
  padding: 0;
  height: 45px;
}

.ovg-modelo-input__field {
  flex: 1;
  border: none;
  outline: none;
  font-family: 'Noto Sans', sans-serif;
  font-size: 15px;
  color: #23201c;
  background: transparent;
  padding: 0 10px;
  height: 100%;
}

.ovg-modelo-input__field::placeholder {
  color: #525252;
}

.ovg-modelo-input__icon {
  padding-right: 10px;
  color: #525252;
  pointer-events: none;
}

.ovg-modelo-clear {
  background: none;
  border: none;
  padding: 0 10px;
  color: #525252;
  cursor: pointer;
  display: flex;
  align-items: center;
  font-size: 14px;
}

.ovg-modelo-clear:hover {
  color: #23201c;
}

/* Spinner en input */
.ovg-modelo-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid #d2d2d2;
  border-top-color: #525252;
  border-radius: 50%;
  margin-right: 10px;
  animation: ovg-spin 0.6s linear infinite;
  flex-shrink: 0;
}

@keyframes ovg-spin {
  to {
    transform: rotate(360deg);
  }
}

.ovg-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #ffffff;
  border: 1px solid #525252;
  border-radius: 5px;
  z-index: 100;
  max-height: 200px;
  overflow-y: auto;
  padding: 4px 0;
}

.ovg-dropdown__item {
  padding: 8px 10px;
  font-size: 15px;
  color: #525252;
  cursor: pointer;
}

.ovg-dropdown__item:hover {
  background: #f5f6fa;
}

.ovg-dropdown__loading {
  padding: 8px 10px;
  font-size: 13px;
  color: #525252;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.ovg-dropdown__spinner {
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 2px solid #d2d2d2;
  border-top-color: #525252;
  border-radius: 50%;
  animation: ovg-spin 0.6s linear infinite;
}

.ovg-dropdown__no-modelo {
  padding: 4px 10px;
  font-size: 15px;
  color: #525252;
  background: #d2d2d2;
  cursor: pointer;
}

.ovg-dropdown__no-modelo:hover {
  background: #c2c2c2;
}

/* Boleta upload */
.ovg-upload {
  border: 1px dashed #525252;
  border-radius: 5px;
  padding: 20px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  min-height: 153px;
  justify-content: center;
}

.ovg-upload--error {
  border-color: #dc3545;
}

.ovg-upload--filled {
  cursor: default;
}

.ovg-upload__icon {
  font-size: 40px;
  color: #1d4ed8;
}

.ovg-upload__text {
  font-size: 15px;
  color: #525252;
  margin: 0;
  text-align: center;
}

.ovg-upload__btn {
  pointer-events: none;
  height: 35px;
  width: 242px;
}

.ovg-upload__note {
  font-size: 12px;
  color: #525252;
  margin: 0;
  text-align: center;
}

.ovg-upload__input {
  display: none;
}

.ovg-upload__preview-img {
  max-height: 120px;
  max-width: 100%;
  border-radius: 4px;
  object-fit: contain;
}

.ovg-upload__preview-file {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #23201c;
}

.ovg-upload__preview-icon {
  font-size: 32px;
  color: #dc3545;
}

.ovg-upload__remove {
  background: none;
  border: 1px solid #525252;
  border-radius: 4px;
  padding: 4px 10px;
  font-size: 13px;
  color: #525252;
  cursor: pointer;
}

/* Card footer */
.ovg-card__footer {
  display: flex;
  justify-content: flex-end;
}

.ovg-card__footer--split {
  justify-content: space-between;
}

/* Shared buttons */
.ovg-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 45px;
  padding: 10px 20px;
  border-radius: 8px;
  font-family: 'Noto Sans', sans-serif;
  font-size: 15px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: opacity 0.15s;
  white-space: nowrap;
}

.ovg-btn:hover {
  opacity: 0.9;
}

.ovg-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.ovg-btn--primary {
  background: #fece2f;
  color: #23201c;
}

.ovg-btn--secondary {
  background: #f5f6fa;
  color: #23201c;
}

/* Select */
.ovg-select-wrap {
  position: relative;
}

.ovg-select {
  appearance: none;
  -webkit-appearance: none;
  padding-right: 36px;
  cursor: pointer;
}

.ovg-select-wrap__icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #525252;
  pointer-events: none;
}

/* Textarea */
.ovg-textarea {
  height: 90px;
  resize: vertical;
  padding-top: 10px;
}

/* Contact button */
.ovg-contact-btn {
  position: fixed;
  bottom: 90px;
  right: 70px;
  z-index: 10;
  width: 45px;
  height: 45px;
  background: #fece2f;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #23201c;
  font-size: 20px;
  text-decoration: none;
}

/* Page footer */
.ovg-page__footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1;
  font-size: 15px;
  font-weight: 400;
  color: #23201c;
  text-align: center;
  margin: 0;
  padding: 12px 0;
  border-top: 1px solid #d2d2d2;
  background: #f5f6fa;
}

/* Mobile progress bar — oculto en desktop */
.ovg-progress {
  display: none;
}

.ovg-progress__track {
  width: 100%;
  height: 8px;
  background: #e3e3e3;
  border-radius: 4px;
  overflow: hidden;
}

.ovg-progress__fill {
  height: 100%;
  background: #fece2f;
  border-radius: 4px;
}

.ovg-progress__label {
  font-size: 14px;
  font-weight: 400;
  color: #525252;
}

/* ============================================================
   MOBILE — max-width: 767px
   ============================================================ */
@media (max-width: 767px) {
  /* Page */
  .ovg-page {
    padding-bottom: 100px;
  }

  /* Header */
  .ovg-header {
    padding: 0 20px;
  }

  /* Sub-header */
  .ovg-subheader {
    min-height: auto;
    padding: 24px 20px;
  }

  .ovg-subheader__inner {
    width: 100%;
  }

  .ovg-subheader__title {
    font-size: 24px;
  }

  .ovg-subheader__subtitle {
    font-size: 14px;
  }

  /* Content */
  .ovg-content {
    width: 100%;
    max-width: 100%;
    margin-top: -20px;
    padding: 0 20px;
    flex-direction: column;
  }

  /* Ocultar panel resumen lateral */
  .ovg-content >>> .ovg-resumen {
    display: none;
  }

  /* Card */
  .ovg-card {
    width: 100%;
    max-width: 375px;
    margin: 0 auto;
    border-radius: 16px;
    padding: 20px;
  }

  /* Desktop step indicator → oculto */
  .ovg-steps {
    display: none;
  }

  /* Mobile progress bar → visible */
  .ovg-progress {
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 100%;
  }

  /* Form rows → columna */
  .ovg-row {
    flex-direction: column;
    gap: 16px;
  }

  /* Campo mitad → todo el ancho */
  .ovg-field--half {
    flex: 1;
    max-width: 100%;
  }

  /* Sección upload boleta en mobile */
  .ovg-upload {
    min-height: 120px;
  }

  /* Ocultar texto "arrastra y suelta" en mobile */
  .ovg-upload__text {
    display: none;
  }

  /* Botón upload → ocupa el ancho */
  .ovg-upload__btn {
    width: 100%;
    pointer-events: auto;
  }

  /* Botones footer → ancho completo con gap */
  .ovg-card__footer--split {
    justify-content: space-between;
    gap: 12px;
  }

  .ovg-card__footer--split .ovg-btn {
    flex: 1;
  }

  /* Siguiente Paso → azul marino en mobile */
  .ovg-card__footer--split .ovg-btn--primary {
    background: #203859;
    color: #ffffff;
  }

  /* Footer de página → oculto en mobile */
  .ovg-page__footer {
    display: none;
  }
}

/* Modal backdrop */
.ovg-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

/* Modal */
.ovg-modal {
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0px 4px 2px rgba(0, 0, 0, 0.25);
  padding: 20px;
  width: 520px;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.ovg-modal__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.ovg-modal__title {
  font-size: 20px;
  font-weight: 700;
  color: #23201c;
  margin: 0;
  flex: 1;
}

.ovg-modal__close {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 18px;
  color: #525252;
  padding: 0;
  flex-shrink: 0;
  line-height: 1;
}

.ovg-modal__subtitle {
  font-size: 15px;
  font-weight: 400;
  color: #525252;
  margin: 0;
  line-height: 1.5;
}

.ovg-modal__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.ovg-modal__footer--center {
  justify-content: center;
}

/* Confirmación */
.ovg-modal--confirmacion {
  align-items: center;
  text-align: center;
}

.ovg-modal__check {
  font-size: 74px;
  color: #22c55e;
}

.ovg-modal__confirm-text {
  font-size: 15px;
  font-weight: 400;
  color: #525252;
  margin: 0;
  line-height: 1.5;
  max-width: 480px;
}
</style>
